/**
 * A pilot's career, as one plain-JSON value.
 *
 * The field set deliberately mirrors the pilot save file the game itself keeps.
 * `MBPLTREC.DOC`'s documented `ROSTER.DAT` record (docs/formats/missions-campaign.md
 * § *`ROSTER.DAT`*) holds: name, rank, service, status, personal aircraft type,
 * wing and fuselage colour, current month and day, missions flown, squadron
 * code, promotion and decoration counters, Boards of Inquiry, decorations with
 * victory thresholds, points, kill counts split by aircraft / balloon /
 * Zeppelin, friendly kills, and one flag per named ace killed. Every one of
 * those has a field below.
 *
 * We are **not** binary-compatible — our record is our own, versioned, JSON,
 * and it carries things the original's 90 bytes did not (unconfirmed claims, a
 * dated log, a recovery date for a hospital stay). Mirroring the field set is
 * about being faithful to *what a career is*, not to how it was packed.
 *
 * Everything here is serialisable and immutable: every transition in this
 * layer returns a new `CareerState` rather than mutating one, so a save is
 * `JSON.stringify(career)` and a load is a parse plus a version check.
 */

import type { AircraftKey } from "../flight/aircraft-table.ts";
import { MissionDraw } from "../mission/mission-build.ts";
import type { Nation } from "../mission/mission-roster.ts";
import type { MissionRegion } from "../mission/mission-world.ts";
import { frontLineScouts } from "./aircraft-service.ts";
import { ENLISTMENT_DATES, formatCareerMonth, type CareerDate, type EnlistmentPeriod } from "./calendar.ts";
import { SERVICE_LABELS, rankOf, serviceFor } from "./service.ts";
import { requireSquadron, startingSquadrons, type Squadron } from "./squadrons.ts";

export const CAREER_STATUSES = ["active", "hospital", "pow", "dead", "discharged", "retired"] as const;

export type CareerStatus = (typeof CAREER_STATUSES)[number];

/**
 * VIEW PILOT RECORD shows one of four words, verbatim: Active, Retired,
 * Discharged, Deceased. A hospital stay and a prison camp are both still
 * "Active" on the record — they are where the pilot is, not what he is.
 */
export const PILOT_RECORD_STATUS: Readonly<Record<CareerStatus, string>> = {
  active: "Active",
  hospital: "Active",
  pow: "Active",
  dead: "Deceased",
  discharged: "Discharged",
  retired: "Retired",
};

export interface VictoryTally {
  readonly aircraft: number;
  readonly balloons: number;
  readonly zeppelins: number;
}

export const NO_VICTORIES: VictoryTally = { aircraft: 0, balloons: 0, zeppelins: 0 };

export interface MedalAward {
  readonly medalId: string;
  /** 0 for the plain award, 1 for "with Bar", 2 for "with Two Bars". */
  readonly bars: number;
  readonly date: CareerDate;
  /** Confirmed victories standing when it was awarded. */
  readonly victories: number;
}

/**
 * `PAINT AIRCRAFT` → `WHICH COLOR?`, verbatim `PS.EXE` colour list — **eight**
 * entries, one contiguous string block at `PS.EXE` `0x3DF6A`.
 *
 * They are not free-choice colours: each is slot `n` of `RB.EXE`'s 17-entry
 * paint table (`DGROUP 0x0A7D`), and the pilot record stores the *slot index*,
 * which the engine expands into the six `.CLG` group colours. The order here is
 * therefore load-bearing — index into `SHIPPED_PAINT_SHOP_COLOURS` with it.
 * See docs/formats/shading-and-paint.md §10.
 *
 * (An earlier revision of this list had six, dropping `Red` and `Tan`; the
 * eight are what the executable ships and what slots 0-7 resolve to.)
 */
export const PAINT_COLORS = ["Blue", "Red", "Yellow", "Olive", "Tan", "Black", "Grey", "White"] as const;

export type PaintColor = (typeof PAINT_COLORS)[number];

/** The paint-table slot a colour name occupies, or `undefined` for a name off the list. */
export function paintColorSlot(color: PaintColor | undefined): number | undefined {
  if (color === undefined) return undefined;
  const slot = PAINT_COLORS.indexOf(color);
  return slot < 0 ? undefined : slot;
}

export interface PersonalAircraft {
  readonly aircraft: AircraftKey;
  readonly wings: PaintColor | undefined;
  readonly fuselage: PaintColor | undefined;
  readonly issuedOn: CareerDate;
}

export interface CareerLogEntry {
  readonly date: CareerDate;
  readonly text: string;
}

/** Bumped when the shape changes so an old save is rejected rather than misread. */
export const CAREER_SAVE_VERSION = 1;

export interface CareerState {
  readonly version: number;
  readonly id: string;
  readonly name: string;
  readonly nation: Nation;
  readonly enlistment: EnlistmentPeriod;
  readonly seed: number;
  readonly date: CareerDate;
  readonly status: CareerStatus;
  /** Index into `CAREER_RANKS[nation]`. */
  readonly rank: number;
  readonly squadronId: string;
  readonly aerodrome: string;
  readonly region: MissionRegion;
  /** The type the squadron has him on, unless a personal aircraft supersedes it. */
  readonly aircraft: AircraftKey;
  readonly personalAircraft: PersonalAircraft | undefined;
  readonly missionsFlown: number;
  readonly confirmed: VictoryTally;
  /** Claims nobody could witness or find the wreck of. Recorded, never counted. */
  readonly unconfirmed: number;
  readonly squadronVictories: number;
  readonly careerScore: number;
  readonly medals: readonly MedalAward[];
  readonly wounds: number;
  /** Boards of Inquiry attended. Three ends the career. */
  readonly strikes: number;
  readonly friendlyKills: number;
  /** `mission-roster.ts` ids of named aces this pilot shot down. */
  readonly acesDowned: readonly string[];
  /** Set while in hospital or a prison camp: the date he is back on duty. */
  readonly dutyReturnDate: CareerDate | undefined;
  /** When he joined his present squadron — the four-month transfer cooldown counts from here. */
  readonly postedOn: CareerDate;
  /** Ace challenges turned down; kept so declining is deterministic, not a reroll. */
  readonly declinedChallenges: number;
  readonly log: readonly CareerLogEntry[];
}

export interface EnlistmentChoices {
  readonly name: string;
  readonly nation: Nation;
  readonly period: EnlistmentPeriod;
}

function careerId(name: string, seed: number): string {
  const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return `${slug || "pilot"}-${seed >>> 0}`;
}

function startingSquadron(nation: Nation, date: CareerDate, draw: MissionDraw): Squadron {
  const candidates = startingSquadrons(nation, date);
  if (candidates.length === 0) throw new Error(`no starting squadron for ${nation}`);
  return draw.pick(candidates);
}

export function createCareer(choices: EnlistmentChoices, seed: number): CareerState {
  const date = ENLISTMENT_DATES[choices.period];
  const draw = new MissionDraw(seed, "enlist");
  const unit = startingSquadron(choices.nation, date, draw);
  const aircraft = draw.pick(frontLineScouts(choices.nation, date));
  const name = choices.name.trim() || "Pilot";
  return {
    version: CAREER_SAVE_VERSION,
    id: careerId(name, seed),
    name,
    nation: choices.nation,
    enlistment: choices.period,
    seed: seed >>> 0,
    date,
    status: "active",
    rank: 0,
    squadronId: unit.id,
    aerodrome: unit.aerodrome,
    region: unit.region,
    aircraft,
    personalAircraft: undefined,
    missionsFlown: 0,
    confirmed: NO_VICTORIES,
    unconfirmed: 0,
    squadronVictories: 0,
    careerScore: 0,
    medals: [],
    wounds: 0,
    strikes: 0,
    friendlyKills: 0,
    acesDowned: [],
    dutyReturnDate: undefined,
    postedOn: date,
    declinedChallenges: 0,
    log: [{ date, text: enlistmentSentence(name, choices.nation, 0, date) }],
  };
}

/**
 * `BACKUP CAREER` — the manual's own sanctioned save-scum, as a career-state
 * operation.
 *
 * The manual describes it as a *duplicate pilot*, not a save slot: *"enter the
 * name under which you wish to backup the career … This backup career may be
 * restored from the Continue A Career option in the Career Menu"*, and the note
 * beneath it draws the distinction the port had backwards — *"A pilot's most
 * current career progress is automatically saved upon exiting the Aerodrome
 * menu. Backup Career is used to save a separate version of the current career
 * for later restoring. This is useful for undoing a mistake you've made in a
 * mission."* So the autosave and the backup are two different things, and the
 * restore path needs no screen of its own: the copy simply appears on the
 * career roster under its new name.
 *
 * The **seed is deliberately carried over**. A career's seed is its identity —
 * every draw the campaign makes for this pilot runs off it — so a backup that
 * re-seeded would be a different man with the same logbook. Carrying it means
 * the id (`careerId`, name-slug plus seed) differs only by the name, which is
 * exactly the intent: backing up twice under one name replaces the earlier
 * copy rather than filling the roster with near-duplicates.
 */
export function backupCareer(career: CareerState, name: string): CareerState {
  const trimmed = name.trim() || "Pilot";
  return { ...career, id: careerId(trimmed, career.seed), name: trimmed };
}

/** Verbatim `PS.EXE`: `"In [date], [name], [rank] enlisted in the [service]."` */
export function enlistmentSentence(name: string, nation: Nation, rank: number, date: CareerDate): string {
  const service = SERVICE_LABELS[serviceFor(nation, date)];
  return `In ${formatCareerMonth(date)}, ${name}, ${rankOf(nation, rank).title} enlisted in the ${service}.`;
}

export function totalVictories(career: CareerState): number {
  return career.confirmed.aircraft + career.confirmed.balloons + career.confirmed.zeppelins;
}

export function squadronOf(career: CareerState): Squadron {
  return requireSquadron(career.squadronId);
}

/** The aeroplane he actually flies: his own if he has one, the squadron's otherwise. */
export function currentAircraft(career: CareerState): AircraftKey {
  return career.personalAircraft?.aircraft ?? career.aircraft;
}

export function isOnDuty(career: CareerState): boolean {
  return career.status === "active";
}

/** A career that has ended, one way or another, and cannot fly again. */
export function isCareerOver(career: CareerState): boolean {
  return career.status === "dead" || career.status === "discharged" || career.status === "retired";
}

export function appendLog(career: CareerState, text: string, date: CareerDate = career.date): CareerState {
  return { ...career, log: [...career.log, { date, text }] };
}

/**
 * Parses a saved career, returning `undefined` for anything that is not one of
 * ours at this version — a save from an older shape is rejected rather than
 * half-read into a career with missing fields.
 */
export function parseCareer(raw: string): CareerState | undefined {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return undefined;
  }
  return asCareer(value);
}

function asCareer(value: unknown): CareerState | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const candidate = value as Partial<CareerState>;
  if (candidate.version !== CAREER_SAVE_VERSION) return undefined;
  const complete =
    typeof candidate.id === "string" &&
    typeof candidate.name === "string" &&
    typeof candidate.seed === "number" &&
    typeof candidate.rank === "number" &&
    typeof candidate.squadronId === "string" &&
    typeof candidate.date === "object" &&
    Array.isArray(candidate.medals);
  return complete ? (candidate as CareerState) : undefined;
}

export function serializeCareer(career: CareerState): string {
  return JSON.stringify(career);
}
